import {BehaviorSubject, Observable} from 'rxjs';

import {LocalStorageService} from './local-storage.service';
import {CredentialsManager} from './credentils.manager';
import {Injectable} from '@angular/core';
import {SecFEPermissionPartnerAppEnum} from '../../shared/enums/sec-f-e-permission-partner-app-enum';
import {WhoAmIHttpService} from './who-am-i-http.service';
import {ISafeCredential} from '../interfaces/safe-credential.interface';
import {SecFEPermissionPartnerAppMappingEnum} from '../../shared/enums/sec-f-e-permission-partner-app-mapping-enum';
import {PermissionGroupPartModel} from '../../modules/auth/models/login-response-model-new';
import {UserDetailsModel} from '../../modules/auth/models/user-details-model';
import {AuthenticationService} from '../../modules/auth/services/authentication.service';
import {Router} from '@angular/router';
import {AppConstants} from '../app-constants';
import {AppEventsService} from './app-events.service';

@Injectable()
export class UserContextService {


    private userDetailsSubject = new BehaviorSubject<UserDetailsModel>(null);
    public userDetails$: Observable<UserDetailsModel> = this.userDetailsSubject.asObservable();

    // private userDetails: IAppUserDetails;
    private relatedContactEmail: string;
    private isInitialized: boolean = false;
    private permissionsFE: string[] = [];


    constructor(
        private router: Router,
        // private authenticationService: AuthenticationService, // circular dependency
        private localStorageService: LocalStorageService,
        private credentialsManager: CredentialsManager,
        private whoAmIHttpService: WhoAmIHttpService,
        private appEventsService: AppEventsService
    ) {
        this.userDetailsSubject.next(this.localStorageService.getUserDetails());
    }

    getUserId(): number {
        return this.getUserDetails()?.id;
    }

    saveRelatedContactEmail(relatedContactEmail: string): void {
        this.relatedContactEmail = relatedContactEmail;
    }


    saveUserDetails(userDetails: UserDetailsModel) {
        this.localStorageService.saveUserDetails(userDetails);
        this.userDetailsSubject.next(userDetails);
    }

    getUserDetails(): UserDetailsModel | null {
        return this.userDetailsSubject.value;
    }

    removeUserDetails() {
        // console.warn('removeUserDetails');
        this.localStorageService.removeUserDetails();
        this.userDetailsSubject.next(null);
    }

    getRelatedContactEmail(): string {
        return this.relatedContactEmail;
    }



    async processWhoAmIResponse(whoAmIResponse: UserDetailsModel): Promise<void> {

        console.log('whoAmIResponse', whoAmIResponse);


        if (!AuthenticationService.isAuthenticatedForCurrentAppFn(whoAmIResponse.permissionGroups)) {
            console.warn('User is not authenticated for current app, redirecting to login page');
            this.removeUserDetails();
            this.localStorageService.removeAuthTokens();

            await this.router.navigate([AppConstants.LOGIN_URL]);
        }

        // clear permissions, it forces to recalculate permissions
        this.permissionsFE = [];


        this.saveUserDetails(whoAmIResponse);

        this.saveRelatedContactEmail(whoAmIResponse.relatedContactEmail);

        let credentials = [  // todo remove in future, credential logout was disabled (not fully removed yet ) in changes23, maybe is not needed anymore
            {
                'provider': 'itscope',
                'login': null,
                'securitySet': false
            }
        ] as ISafeCredential[];
        this.credentialsManager.saveCredentials(credentials);

    }


    async reloadUserDetails(forced = false): Promise<void> {
        console.log('reloadUserDetails is called');

        let userDetails = this.getUserDetails();

        if (!this.isInitialized || forced || !userDetails) {
            try {
                // const response = await this.appAuthHttpService.loadUserDetails(this.getUserId());
                const whoAmIResponse: UserDetailsModel = await this.whoAmIHttpService.whoAmI();
                await this.processWhoAmIResponse(whoAmIResponse);
                this.isInitialized = true;
            } catch (error) {
                console.log('Error reloading user details, logout called');
                this.appEventsService.announceAppEvent(this.appEventsService.APP_EVENT_ENUM.LOGOUT);
                throw error;
            }
        }

        console.log('reloadUserDetails', this.getUserDetails());
    }


    // return true if the user is not in CSP group and has the read only flag
    isCspReadOnly(): boolean {
        // return this.userDetails.contractsReadOnlyEnabled;
        return this.hasPermission(SecFEPermissionPartnerAppEnum.PAU_CONTRACTS_CSP_READONLY);
    }


    // returns FE permissions based on BE permission groups
    calculateFEPermissions() {
        console.log('calculateFEPermissions');

        let userDetails = this.getUserDetails();
        this.permissionsFE = this.getFEPermissionsFromBEPermissionGroups(userDetails?.permissionGroups || []);
        // console.log('this.permissionsFE ', this.permissionsFE );

        if (userDetails?.primaryUser || userDetails?.isRepresentative) {
            this.permissionsFE.push(SecFEPermissionPartnerAppEnum.MY_USER_MANAGEMENT); // show user management menu item
        }
    }

    getFEPermissionsFromBEPermissionGroups(permissionsGroups: PermissionGroupPartModel[]): string[] {
        let debug = false;
        let permissionsBE: string[] = [];
        permissionsGroups.forEach(group => {
            permissionsBE = permissionsBE.concat(group.permissions);
        });
        permissionsBE = [...new Set(permissionsBE)]; // remove duplicates

        if (debug) {
            console.log('permissionsBE', permissionsBE);
        }


        let permissionsFE: string[] = [];

        // add to permissionsFE all permissions which is defined in permissionsBE and and also in SecBEPermissionPartnerAppEnum
        permissionsBE.forEach(permission => {
            if (SecFEPermissionPartnerAppEnum[permission]) {
                permissionsFE.push(permission);
            } else if (SecFEPermissionPartnerAppMappingEnum[permission]) {
                permissionsFE.push(permission);
            } else {
                if (debug) {
                    console.warn('Permission not found in SecFEPermissionPartnerAppEnum:', permission);
                }
            }
        });


        permissionsFE.forEach(permisson => SecFEPermissionPartnerAppMappingEnum[permisson] && permissionsFE.push(SecFEPermissionPartnerAppMappingEnum[permisson]));
        // has to be processed 2 times, so once again:
        permissionsFE.forEach(permisson => SecFEPermissionPartnerAppMappingEnum[permisson] && permissionsFE.push(SecFEPermissionPartnerAppMappingEnum[permisson]));
        permissionsFE = [...new Set(permissionsFE)];

        if (debug) {
            console.log('permissionsFE', permissionsFE);
        }

        return permissionsFE;
    }

    getPermissions(): string[] {
        if (!this.permissionsFE.length) {
            this.calculateFEPermissions();
        }
        return this.permissionsFE;
    }

    // Check if the user has any of the required permissions
    hasPermission(requiredPermissions?: string[] | string): boolean {
        const userPermissions = this.getPermissions();

        if (!requiredPermissions || requiredPermissions.length === 0) {
            // console.warn('Permissions are not specified yet');
            return true; // No permissions required mean authorized
        }

        const permissionsToCheck = Array.isArray(requiredPermissions)
            ? requiredPermissions
            : [requiredPermissions];

        return permissionsToCheck.some(permission => userPermissions.includes(permission));
    }

    hasAllPermissions(requiredPermissions?: string[] | string): boolean {
        const userPermissions = this.getPermissions();

        if (!requiredPermissions || requiredPermissions.length === 0) {
            // console.warn('Permissions are not specified yet');
            return true; // No permissions required mean authorized
        }

        const permissionsToCheck = Array.isArray(requiredPermissions)
            ? requiredPermissions
            : [requiredPermissions];

        return permissionsToCheck.every(permission => userPermissions.includes(permission));
    }

    getDealerPortalSecurityToken(): string {
        return this.localStorageService.getDealerPortalSecurityToken();
    }

    getAppPortalSecurityToken(): string {
        return this.localStorageService.getAppPortalSecurityToken();
    }
}
